import { ArrowLeftOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Flex, Form, Input, Space, Typography, theme } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthCard } from "../components/AuthCard";
import { AUTH_ERROR_CODE } from "../common/types";
import { usePreferences } from "../hooks/usePreferences";
import { getAuthErrorCode, isLocalHost, requestPasswordReset } from "../lib/http";
import { useNotifier } from "../providers/NotificationProvider";

interface ForgotPasswordState {
  username?: string;
  db?: string;
}

function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const notifier = useNotifier();
  const { token } = theme.useToken();
  const { currentLanguage, direction } = usePreferences();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  // Local development only: the API echoes the link back when no mail can be sent.
  const [localResetUrl, setLocalResetUrl] = useState<string>();

  // Login hands over whatever the user had already typed.
  const handoff = (location.state ?? {}) as ForgotPasswordState;

  useEffect(() => {
    if (handoff.username) {
      form.setFieldValue("username", handoff.username);
    }
    if (handoff.db) {
      form.setFieldValue("db", handoff.db);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backToLogin = () => {
    const redirect = searchParams.get("redirect");
    navigate({
      pathname: "/",
      search: redirect ? `?redirect=${encodeURIComponent(redirect)}` : "",
    });
  };

  const onFinish = async (values: { username: string; db?: string }) => {
    setLoading(true);
    try {
      const response = await requestPasswordReset({
        username: values.username,
        db: values.db,
        lang: currentLanguage,
      });
      setLocalResetUrl(response.data?.resetUrl);
      setSent(true);
    } catch (error) {
      const code = getAuthErrorCode(error);
      notifier.error({
        message: t("auth.forgotPassword.error"),
        description:
          code === AUTH_ERROR_CODE.MAIL_NOT_CONFIGURED
            ? t("auth.forgotPassword.errorMailNotConfigured")
            : t("auth.forgotPassword.errorDescription"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthCard
        title={t("auth.forgotPassword.sentTitle")}
        subtitle={t("auth.forgotPassword.sentSubtitle")}
      >
        <Flex vertical gap={16} align="center">
          <MailOutlined style={{ fontSize: 48, color: token.colorSuccess }} />
          <Typography.Text type="secondary" style={{ textAlign: "center" }}>
            {t("auth.forgotPassword.sentHint")}
          </Typography.Text>
          {localResetUrl && (
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Typography.Text strong>
                {t("auth.forgotPassword.localLink")}
              </Typography.Text>
              <Typography.Link
                href={localResetUrl}
                style={{ wordBreak: "break-all" }}
              >
                {localResetUrl}
              </Typography.Link>
            </Space>
          )}
          <Button type="primary" block size="large" onClick={backToLogin}>
            {t("auth.logout.backToLogin")}
          </Button>
        </Flex>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t("auth.forgotPassword.title")}
      subtitle={t("auth.forgotPassword.subtitle")}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        size="large"
        onFinish={onFinish}
      >
        {isLocalHost() && (
          <Form.Item
            label={t("auth.login.db")}
            name="db"
            rules={[
              { required: true, message: t("auth.forgotPassword.dbRequired") },
            ]}
          >
            <Input
              placeholder={t("auth.login.dbPlaceholder")}
              dir={direction === "rtl" ? "rtl" : "ltr"}
            />
          </Form.Item>
        )}

        <Form.Item
          label={t("auth.forgotPassword.username")}
          name="username"
          rules={[
            {
              required: true,
              message: t("auth.forgotPassword.usernameRequired"),
            },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t("auth.forgotPassword.usernamePlaceholder")}
            autoComplete="username"
            inputMode="email"
            dir={direction === "rtl" ? "rtl" : "ltr"}
          />
        </Form.Item>

        <Button
          loading={loading}
          disabled={loading}
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={{ marginTop: 8 }}
        >
          {t("auth.forgotPassword.submit")}
        </Button>

        <Button
          type="link"
          block
          icon={<ArrowLeftOutlined rotate={direction === "rtl" ? 180 : 0} />}
          onClick={backToLogin}
          style={{ marginTop: 12 }}
        >
          {t("auth.logout.backToLogin")}
        </Button>
      </Form>
    </AuthCard>
  );
}

export default ForgotPassword;
